import React, { Suspense, lazy, useEffect, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ToastProvider } from "./lib/toast";
import { useAuth } from "./lib/auth";
import { Spinner } from "./components/ui/Spinner";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { cn } from "./lib/utils";

const LandingPage = lazy(() => import("./pages/Landing"));
const PricingPage = lazy(() => import("./pages/Pricing"));
const AboutPage = lazy(() => import("./pages/About"));
const LoginPage = lazy(() => import("./pages/Login"));
const RegisterPage = lazy(() => import("./pages/Register"));
const PrivacyPage = lazy(() => import("./pages/Privacy"));
const TermsPage = lazy(() => import("./pages/Terms"));
const DmcaPage = lazy(() => import("./pages/Dmca"));
const AUPage = lazy(() => import("./pages/AUP"));
const HelpPage = lazy(() => import("./pages/Help"));
const GuidePage = lazy(() => import("./pages/Guide"));
const ContactPage = lazy(() => import("./pages/Contact"));
const ApiDocsPage = lazy(() => import("./pages/ApiDocs"));
const NotFoundPage = lazy(() => import("./pages/NotFound"));
const AppShell = lazy(() => import("./pages/app/AppShell"));
const InboxPage = lazy(() => import("./pages/app/Inbox"));
const ThreadPage = lazy(() => import("./pages/app/Thread"));
const ComposePage = lazy(() => import("./pages/app/Compose"));
const SettingsPage = lazy(() => import("./pages/app/Settings"));
const SettingsProfile = lazy(() => import("./pages/app/Settings").then(m => ({ default: m.SettingsProfile })));
const SettingsAliases = lazy(() => import("./pages/app/Settings").then(m => ({ default: m.SettingsAliases })));
const SettingsSubscription = lazy(() => import("./pages/app/Settings").then(m => ({ default: m.SettingsSubscription })));
const ContactsPage = lazy(() => import("./pages/app/Contacts"));
const CalendarPage = lazy(() => import("./pages/app/Calendar"));
const PendingActivationPage = lazy(() => import("./pages/PendingActivation"));

function PageFallback() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <Spinner size="lg" />
    </div>
  );
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <PageFallback />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return <>{children}</>;
}

function RequireProductAccess({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <PageFallback />;
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  if (!user.accessEnabled) return <Navigate to="/pending-activation" replace />;
  return <>{children}</>;
}

function PublicOnly({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <PageFallback />;
  if (user) return <Navigate to={user.accessEnabled ? "/app" : "/pending-activation"} replace />;
  return <>{children}</>;
}

function AnimatedRoutes() {
  const location = useLocation();
  React.useEffect(() => {
    import("@/lib/telemetry").then(({ telemetry }) => telemetry.page(location.pathname));
  }, [location.pathname]);
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
      >
        <Suspense fallback={<PageFallback />}>
          <Routes location={location}>
            <Route path="/" element={<LandingPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/login" element={<PublicOnly><LoginPage /></PublicOnly>} />
            <Route path="/register" element={<PublicOnly><RegisterPage /></PublicOnly>} />
            <Route path="/pending-activation" element={<RequireAuth><PendingActivationPage /></RequireAuth>} />
            <Route path="/legal/privacy" element={<PrivacyPage />} />
            <Route path="/legal/terms" element={<TermsPage />} />
            <Route path="/legal/dmca" element={<DmcaPage />} />
            <Route path="/legal/acceptable-use" element={<AUPage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/help/guide" element={<GuidePage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/help/api" element={<ApiDocsPage />} />

            <Route
              path="/app"
              element={
                <RequireProductAccess>
                  <AppShell />
                </RequireProductAccess>
              }
            >
              <Route index element={<Navigate to="inbox" replace />} />
              <Route path=":folder" element={<InboxPage />} />
              <Route path=":folder/:threadId" element={<ThreadPage />} />
              <Route path="compose" element={<ComposePage />} />
              <Route path="contacts" element={<ContactsPage />} />
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="settings" element={<SettingsPage />}>
                <Route index element={<SettingsProfile />} />
                <Route path="profile" element={<SettingsProfile />} />
                <Route path="aliases" element={<SettingsAliases />} />
                <Route path="subscription" element={<SettingsSubscription />} />
              </Route>
            </Route>

            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </motion.div>
    </AnimatePresence>
  );
}

export default function App() {
  return (
    <ToastProvider>
      <ErrorBoundary>
        <div className={cn("min-h-full font-sans")}>
          <AnimatedRoutes />
        </div>
      </ErrorBoundary>
    </ToastProvider>
  );
}
